import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { ICommandBarItemProps } from '@fluentui/react/lib/CommandBar';
import { DefaultPalette } from 'office-ui-fabric-react';
import { t } from 'i18next';
import ViewWrapperWithCommandBar from '../Layout/ViewWrapperWithCommandBar';
import { titleCommandBarButtonStyles } from '../../styles/button-styles';
import { NodoOrganigramaDataGrid } from '../../system/organization_chart/nodo-organigrama-datagrid.interface';
import DataEditor, { GridCell, GridCellKind, GridColumn, Item, EditableGridCell, DataEditorRef } from '@glideapps/glide-data-grid';
import TimeTaxSupportSystemClient from '../../system';
import MessageCourier from '../../lib/MessageCourier';
import { User } from '../../system/User';
import Loading from '../Spinners/Loading';
import { OrganizationChartLevel } from '../../system/organization_chart/OrganizationChartLevel';
import { OPERATORS, TYPES } from '../../common/list-utils';
import { CommandBarButton, IComboBoxOption, Icon, IIconProps, IStackStyles, ITextFieldStyles, Text, TextField } from '@fluentui/react';
import { mergeStyles } from '@fluentui/react/lib/Styling';
import '@glideapps/glide-data-grid/dist/index.css';
import { handleColumnResizeUtil } from '../../lib/GlideDataGrid/UtilsGlideGrid';
import ComboBoxSearch from '../Common/ComboBoxSearch';

interface ICostCenterProps {
  system: TimeTaxSupportSystemClient;
  courier: MessageCourier;
  changeBlockUI: (change: boolean) => void;
  user: User;
}

// --- ICON STYLE ---
const iconClass = mergeStyles({
  position: 'relative',
  top: 2,
  left: 4,
  cursor: 'pointer',
});

const textFieldStyles: Partial<ITextFieldStyles> = {
  field: {
    '::placeholder': {
      color: '#aaa',
    },
  },
  subComponentStyles: {
    label: {
      root: {
        padding: '0px',
      },
    },
  },
};

const stackStyles: Partial<IStackStyles> = { root: { height: 32, fontSize: 12 } };
const FilterClearIcon: IIconProps = { iconName: 'ClearFilter' };

const CostCenterView: React.FC<ICostCenterProps> = (props) => {
  const [data, setData] = useState<NodoOrganigramaDataGrid['data']>([]);
  const [originalData, setOriginalData] = useState<NodoOrganigramaDataGrid['data']>([]);
  const [listLevelsFilters, setListLevelsFilters] = useState<
    {
      quick: boolean;
      name: string;
      type: TYPES;
      placeholder: string;
      operator: OPERATORS;
      contextForCombobox: IComboBoxOption[];
    }[]
  >([]);
  const [filters, setFilters] = useState<{ [index: string]: { key: string; value: string } }>({});
  const [levels, setLevels] = useState<OrganizationChartLevel[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchValue, setSearchValue] = useState('');
  const editorRef = useRef<DataEditorRef | null>(null);
  const [columns, setColumns] = useState<GridColumn[]>([]);

  // --- CARGA DE NIVELES ---
  useEffect(() => {
    (async () => {
      props.changeBlockUI(true);
      setLoading(true);
      const levelsResponse = await props.system.getOrganizationChartSystem().getLevels();
      const formattedLevels = await Promise.all(
        levelsResponse.map(async (item) => {
          const organigramaData = await props.system.getOrganizationChartSystem().getLastLevelNodesss(item.getLevel().toString());
          return {
            quick: true,
            name: item.getNameTec(),
            type: TYPES.COMBOBOXTEXT,
            placeholder: t(item.getName()),
            operator: OPERATORS.CONTAINS,
            contextForCombobox: organigramaData.levels,
          };
        }),
      );
      setLevels(levelsResponse);
      setListLevelsFilters(formattedLevels);

      await fetchData();

      props.changeBlockUI(false);
      setLoading(false);
    })();
  }, []);

  // --- efecto debounce para centro de costos ---
  useEffect(() => {
    const handler = setTimeout(() => {
      const newFilters = { ...filters };
      if (searchValue.trim() !== '') {
        newFilters['nor_cecos'] = { key: 'nor_cecos', value: searchValue };
      } else {
        delete newFilters['nor_cecos'];
      }
      setFilters(newFilters);
    }, 500); // espera 500 ms antes de aplicar el filtro

    return () => {
      clearTimeout(handler);
    };
  }, [searchValue]); // se ejecuta cada vez que el usuario escribe

  useEffect(() => {
    if (Object.keys(listLevelsFilters).length > 0) {
      (async () => {
        props.changeBlockUI(true);
        await fetchData();
        props.changeBlockUI(false);
      })();
    }
  }, [filters]);

  // --- CARGA DE DATOS ---
  const fetchData = async () => {
    try {
      const orgSystem = props.system.getOrganizationChartSystem();
      // Construcción dinámica de los filtros activos
      const activeFilters = Object.entries(filters).map(([key, { value }]) => {
        const def = listLevelsFilters.find((f) => f.name === key);

        return {
          name: key,
          value: value,
          operator: def?.operator || 'contains',
        };
      });

      const dataGrid = await orgSystem.getDataGrid(1000, activeFilters);
      setData(dataGrid.data);
      setOriginalData(JSON.parse(JSON.stringify(dataGrid.data)));
    } catch (error) {
      console.error({ error });
    }
  };

  // --- COLUMNAS ---
  useEffect(() => {
    const dynamicColumns: GridColumn[] = levels.map((lvl) => ({
      title: t(lvl.getName()),
      id: lvl.getNameTec(),
      themeOverride: {
        headerFontStyle: '900 14px',
      },
      grow: 1,
    }));

    const baseColumns: GridColumn[] = [
      {
        id: 'nor_cecos',
        title: t('Centro de costo'),
        themeOverride: { bgCell: '#f4f9ff', headerFontStyle: '900 14px' },
        grow: 1,
      },
    ];

    setColumns([...dynamicColumns, ...baseColumns]);
  }, [levels]);

  // --- CELDAS ---
  const getCellContent = useCallback(
    ([col, row]: Item): GridCell => {
      const column = columns[col];
      const item = data[row];
      if (!item || !column) {
        return {
          kind: GridCellKind.Text,
          data: '',
          displayData: '',
          allowOverlay: false,
          readonly: true,
        };
      }
      const value = item[column.id as keyof typeof item];
      const isEditable = column.id === 'nor_cecos';
      return {
        kind: GridCellKind.Text,
        data: String(value ?? ''),
        displayData: String(value ?? ''),
        allowOverlay: isEditable,
        readonly: !isEditable,
      };
    },
    [data, columns],
  );

  const onCellEdited = useCallback(
    (cell: Item, newValue: EditableGridCell) => {
      const [col, row] = cell;
      const column = columns[col];
      if (!column || row >= data.length) return;
      if (column.id !== 'nor_cecos') return;
      const copy = [...data];
      const item = { ...copy[row] };
      item[column.id] = String(newValue.data);
      copy[row] = item;
      setData(copy);
    },
    [columns, data],
  );

  const validateCell = useCallback((cell: Item, newValue: EditableGridCell) => {
    if (newValue.kind === GridCellKind.Text) {
      return /^[0-9]*$/.test(String(newValue.data));
    }
    return true;
  }, []);

  // --- GUARDAR ---
  const handleSave = useCallback(async () => {
    try {
      props.changeBlockUI(true);
      const payload = data
        .filter((current, index) => {
          const original = originalData[index];
          if (!original) return true;
          const oldValue = (original.nor_cecos ?? '').trim();
          const newValue = (current.nor_cecos ?? '').trim();
          return oldValue !== newValue;
        })
        .map((d) => ({
          nor_id: d.nor_id,
          nor_cecos: d.nor_cecos?.trim() === '' ? null : d.nor_cecos,
        }));

      if (payload.length === 0) {
        props.courier.messageInfo(t('No hay cambios para guardar'));
        return;
      }

      const orgSystem = props.system.getOrganizationChartSystem();
      const response = await orgSystem.saveCostCenters(payload);

      if (response.success) {
        props.changeBlockUI(true);
        await fetchData();
        props.changeBlockUI(false);
        const isSingle = payload.length === 1;
        props.courier.messageSuccess(`${response.updated} ${t(isSingle ? 'centro de costo actualizado correctamente' : 'centros de costo actualizados correctamente')}`);
      } else {
        props.courier.messageError(`Algunos registros fallaron: ${response.errors.join(', ')}`);
      }
    } catch (error) {
      console.error(error);
      props.courier.messageError(t('Error al guardar los centros de costo'));
    } finally {
      props.changeBlockUI(false);
    }
  }, [data, originalData]);

  // --- MENÚ ---
  const itemsMenu = useMemo(
    (): ICommandBarItemProps[] => [
      {
        key: 'title',
        text: t('Centro de costos'),
        iconProps: { iconName: 'Backlog', color: DefaultPalette.themeLighterAlt },
        buttonStyles: titleCommandBarButtonStyles,
      },
      {
        key: 'save',
        text: t('Guardar'),
        iconProps: { iconName: 'Save', color: DefaultPalette.themeLighterAlt },
        buttonStyles: titleCommandBarButtonStyles,
        onClick: () => void handleSave(),
        disabled: loading,
      },
    ],
    [handleSave, loading],
  );

  // --- ELIMINAR FILTRO DESDE CHIP ---
  const removeFilter = (filterKey: string) => {
    const newFilters = { ...filters };
    delete newFilters[filterKey];
    setFilters(newFilters);
    if (filterKey === 'nor_cecos') {
      setSearchValue('');
    }
  };

  const clearFilters = () => {
    setFilters({});
    setSearchValue('');
  };

  const getOptionByValues = (opt: any, context: any): string[] => {
    if (!opt || !context) return [''];
    const found = context.find((o: any) => o.key === opt.key && o.text === opt.value);
    return found ? [found.key] : [''];
  };

  const handleColumnResize = useCallback(
    handleColumnResizeUtil((cols) => setColumns(cols)),
    [],
  );
  if (loading) {
    return <Loading title="Cargando" description="Por favor espere estamos procesando la información necesaria para completar su solicitud." />;
  }

  return (
    <ViewWrapperWithCommandBar title="" items={itemsMenu} withBorderBottom>
      <div className="padding-list">
        <div className="ms-Grid" dir="ltr">
          <div className="ms-Grid-row row-flex-calendar">
            <div className="ms-Grid-col ms-sm12 ms-md12 ms-lg12 ms-xl2 ms-xxl2 filter-menu" dir="ltr">
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, padding: '0 0 8px 0' }}>
                <Text block variant="medium" className="ms-hiddenXlUp">
                  {t('Filtros')}
                </Text>

                <CommandBarButton
                  onClick={clearFilters}
                  className="bg-transparent"
                  allowDisabledFocus
                  styles={stackStyles}
                  iconProps={FilterClearIcon}
                  text={t('Limpiar')}
                />
              </div>

              {/* FILTROS */}
              {listLevelsFilters.length > 0 && (
                <div className="ms-Grid-row" dir="ltr">
                  <div className="ms-Grid-col ms-xl12 ms-sm6 ms-md-12">
                    <TextField
                      className="filter-textfield-list"
                      label={t('Centro de costos')}
                      value={searchValue}
                      onChange={(e, value) => setSearchValue(value || '')}
                      styles={textFieldStyles}
                    />
                  </div>

                  {listLevelsFilters.map((filterConfig) => (
                    <div className="ms-Grid-col ms-xl12 ms-sm6 ms-md-12">
                      <label className="ms-Label label-filter">{filterConfig.placeholder}</label>
                      <ComboBoxSearch
                        key={filterConfig.name}
                        options={filterConfig.contextForCombobox}
                        className="combo-box-filter-detaillist"
                        selected={getOptionByValues(filters[filterConfig.name], filterConfig.contextForCombobox)}
                        onSelectOption={(option) => {
                          const newFilters = { ...filters };
                          if (option) newFilters[filterConfig.name] = { key: option.key.toString(), value: option.text };
                          else delete newFilters[filterConfig.name];
                          setFilters(newFilters);
                        }}
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div
              className="ms-Grid-col ms-sm12 ms-md12 ms-lg12 ms-xl10 ms-xxl10 container-data-grid-cost-center"
              style={{ height: '100vh', width: '100%' }}
            >
              <Text block variant="large" className="text-bold title-filter-wrapper mb-2">
                {t('Centro de costos')}

                {/* TAGS DE FILTROS APLICADOS */}
                {Object.entries(filters).map(([key, { value }]) => {
                  const filterDef = listLevelsFilters.find((f) => f.name === key);
                  const label = filterDef?.placeholder || key;
                  return (
                    <div key={key} className="chip-filter">
                      <label className="chip-filter-label">
                        {label}: {value}
                        <Icon iconName="Cancel" className={iconClass} onClick={() => removeFilter(key)} />
                      </label>
                    </div>
                  );
                })}
              </Text>

              {/* TABLA */}
              <DataEditor
                width="100%"
                ref={editorRef}
                columns={columns}
                maxColumnWidth={1000}
                getCellContent={getCellContent}
                onCellEdited={onCellEdited}
                validateCell={validateCell}
                rows={data.length}
                rowMarkers="none"
                smoothScrollX
                smoothScrollY
                rowHeight={35}
                onColumnResize={handleColumnResize}
                keybindings={{
                  activateCell: false,
                  copy: false,
                  paste: false,
                  delete: false,
                  selectAll: false,
                }}
                editOnType={true}
              />
            </div>
          </div>
        </div>
      </div>
    </ViewWrapperWithCommandBar>
  );
};

export default CostCenterView;
